import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';
import MockExamQuestionRenderer from '../usercomponents/MockExamQuestionRenderer';

const apiUrl = process.env.REACT_APP_API_URL;

function isAnswerEmpty(answer) {
  if (!answer) return true;
  if (typeof answer.text === 'string') return !answer.text.trim();
  if (answer.selected_option_id) return false;
  if (Array.isArray(answer.selected_option_ids)) return answer.selected_option_ids.length === 0;
  if (answer.value !== undefined && answer.value !== null && answer.value !== '') return false;
  return true;
}

function ResultRow({ question, result }) {
  const options = question.options || [];
  const correctIds = result.correctAnswer?.correct_option_ids || [];

  return (
    <div className="card mb-16">
      <div className="card-body">
        <div className="d-flex justify-content-between mb-8">
          <span className="text-13 text-gray-400 text-capitalize">{question.question_type.replace('_', ' ')}</span>
          <span className="fw-semibold">
            {result.requiresManualEvaluation ? 'Manual evaluation' : `${result.marksAwarded} / ${result.maxMarks}`}
          </span>
        </div>
        <div className="fw-semibold mb-12">{question.question_text}</div>

        {['single_choice', 'multiple_choice', 'true_false'].includes(question.question_type) && !result.requiresManualEvaluation && (
          <div className="d-flex flex-column gap-6">
            {options.map((o) => (
              <div key={o.id} className="p-8" style={{ borderRadius: 8, background: correctIds.includes(o.id) ? '#d1e7dd' : 'transparent' }}>
                <span style={{ color: correctIds.includes(o.id) ? '#0f5132' : '#111827', fontWeight: correctIds.includes(o.id) ? 600 : 400 }}>{o.text}</span>
                {correctIds.includes(o.id) && <span className="badge bg-success-subtle text-success-emphasis ms-8">Correct</span>}
              </div>
            ))}
          </div>
        )}

        {['short_text', 'fill_blank'].includes(question.question_type) && !result.requiresManualEvaluation && (
          <div className="text-13 text-gray-500">Accepted: {(result.correctAnswer?.accepted_answers || []).join(', ')}</div>
        )}
        {question.question_type === 'numerical' && !result.requiresManualEvaluation && (
          <div className="text-13 text-gray-500">Correct: {result.correctAnswer?.value} ± {result.correctAnswer?.tolerance}</div>
        )}

        <div className="mt-8">
          {result.requiresManualEvaluation && <span className="text-13" style={{ color: '#997404' }}><i className="ph ph-clock me-1" />Long answer — would require manual evaluation in a real exam</span>}
          {result.isCorrect === true && <span className="text-13" style={{ color: '#198754', fontWeight: 600 }}><i className="ph ph-check-circle me-1" />Correct</span>}
          {result.isCorrect === false && <span className="text-13" style={{ color: '#dc3545', fontWeight: 600 }}><i className="ph ph-x-circle me-1" />Incorrect</span>}
        </div>
        {result.explanation && <div className="text-13 text-gray-500 mt-8"><strong>Explanation:</strong> {result.explanation}</div>}
      </div>
    </div>
  );
}

function AdminMockExamBankPreview() {
  const { bankId } = useParams();
  const [searchParams] = useSearchParams();
  const questionCount = searchParams.get('count') || 10;

  const [previewToken, setPreviewToken] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [view, setView] = useState('taking'); // 'taking' | 'review' | 'result'
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const getToken = () => JSON.parse(sessionStorage.getItem('adminSession'))?.token;

  const startTest = useCallback(async () => {
    setLoading(true);
    setError('');
    setResult(null);
    setCurrentIndex(0);
    setView('taking');
    try {
      const token = getToken();
      const response = await axios.post(
        `${apiUrl}/api/admin/mock-exams/banks/${bankId}/preview-test/start`,
        { questionCount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPreviewToken(response.data.previewToken);
        setQuestions(response.data.questions || []);
        setAnswers({});
      } else {
        setError(response.data.message || 'Failed to start test.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error starting test.');
    } finally {
      setLoading(false);
    }
  }, [bankId, questionCount]);

  useEffect(() => { startTest(); }, [startTest]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const token = getToken();
      const response = await axios.post(
        `${apiUrl}/api/admin/mock-exams/preview-test/submit`,
        { previewToken, answers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setResult(response.data);
        setView('result');
      } else {
        setError(response.data.message || 'Failed to submit test.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error submitting test.');
    } finally {
      setSubmitting(false);
    }
  };

  const currentQuestion = questions[currentIndex];
  const answeredCount = questions.filter((q) => !isAnswerEmpty(answers[q.id])).length;

  const goTo = (idx) => setCurrentIndex(idx);

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><Link to="/admin-mock-exam-banks" className="text-gray-200 fw-normal text-15 hover-text-main-600">Question Banks</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Quick Test</span></li>
              </ul>
            </div>
            {view === 'result' && (
              <button type="button" className="btn btn-outline-main rounded-pill py-9" onClick={startTest}>
                <i className="ph ph-arrow-clockwise me-2" /> Test Again
              </button>
            )}
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}

          {loading ? (
            <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
          ) : view === 'result' && result ? (
            <>
              <div className="card mb-24">
                <div className="card-body p-24 text-center">
                  <div className="text-24 fw-bold text-main-600">{result.totalMarksScored} / {result.totalMarksPossible}</div>
                  <div className="text-gray-500">{result.percentage}%</div>
                </div>
              </div>
              {questions.map((q) => {
                const r = result.results.find((x) => x.id === q.id);
                return <ResultRow key={q.id} question={q} result={r} />;
              })}
            </>
          ) : view === 'review' ? (
            <>
              <div className="alert alert-info mb-20">
                Review your answers before submitting. Click a question number to go back and change it.
              </div>
              <div className="card mb-20">
                <div className="card-body p-24">
                  <div className="text-13 text-gray-500 mb-16">{answeredCount} of {questions.length} answered</div>
                  <div className="d-flex flex-wrap gap-8">
                    {questions.map((q, idx) => {
                      const answered = !isAnswerEmpty(answers[q.id]);
                      return (
                        <button
                          key={q.id}
                          type="button"
                          onClick={() => { setCurrentIndex(idx); setView('taking'); }}
                          className="d-flex align-items-center justify-content-center"
                          style={{
                            width: 40, height: 40, borderRadius: 8, border: 'none',
                            background: answered ? '#198754' : '#e5e7eb',
                            color: answered ? '#fff' : '#374151', fontSize: 14, fontWeight: 600,
                          }}
                        >
                          {idx + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-between">
                <button type="button" className="btn btn-outline-main rounded-pill py-9 px-32" onClick={() => setView('taking')}>
                  Back
                </button>
                <button type="button" className="btn btn-main rounded-pill py-9 px-32" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? 'Scoring...' : 'Submit Test'}
                </button>
              </div>
            </>
          ) : currentQuestion ? (
            <>
              <div className="alert alert-info mb-20">
                Scratch test — not saved anywhere, doesn't affect real exam reports. Answer or skip, then continue.
              </div>
              <div className="text-13 text-gray-500 mb-16">Question {currentIndex + 1} of {questions.length} · {answeredCount} answered</div>
              <div className="card mb-20">
                <div className="card-body p-24">
                  <div className="text-13 text-gray-400 mb-8 text-capitalize">{currentQuestion.question_type.replace('_', ' ')} · {currentQuestion.marks} mark(s)</div>
                  <div className="fw-semibold mb-16">{currentQuestion.question_text}</div>
                  <MockExamQuestionRenderer
                    question={{ ...currentQuestion, options_snapshot: currentQuestion.options }}
                    answer={answers[currentQuestion.id]}
                    onChange={(newAnswer) => setAnswers((prev) => ({ ...prev, [currentQuestion.id]: newAnswer }))}
                  />
                </div>
              </div>
              <div className="d-flex justify-content-between flex-wrap gap-8">
                <button type="button" className="btn btn-outline-main rounded-pill py-9 px-20" disabled={currentIndex === 0} onClick={() => goTo(currentIndex - 1)}>
                  Previous
                </button>
                {currentIndex < questions.length - 1 ? (
                  <button type="button" className="btn btn-main rounded-pill py-9 px-20" onClick={() => goTo(currentIndex + 1)}>
                    {isAnswerEmpty(answers[currentQuestion.id]) ? 'Skip' : 'Next'}
                  </button>
                ) : (
                  <button type="button" className="btn btn-main rounded-pill py-9 px-20" onClick={() => setView('review')}>
                    Review Answers
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminMockExamBankPreview;
